"use strict";
var _a, _b, _c, _d, _e;
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmployeeService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const jwt_1 = require("@nestjs/jwt");
const employee_repository_1 = require("./employee.repository");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const helper_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/helper.utils");
const constants_1 = require("../../../../../../libs/service-lib/src/lib/constants");
const lookup_validation_1 = require("../../../../service-lib/src/lib/utils/lookup-validation");
const masters_validation_1 = require("../../../../service-lib/src/lib/utils/masters-validation");
const password_util_1 = require("../../../../service-lib/src/lib/utils/password.util");
const notification_utils_1 = require("../../../../service-lib/src/lib/utils/notification.utils");
const environment_1 = require("../../../../service-lib/src/lib/environment");
const axios_1 = tslib_1.__importDefault(require("axios"));
const constants_2 = require("../../../../service-lib/src/lib/constants");
// import { NOTIFICATION_EMAIL, PASSWORD_RESET_EVENT } from "apps/services/service-lib/src/lib/constants";
let EmployeeService = class EmployeeService {
    constructor(employeeRepository, lookUpValidation, masterValidation, jwtService, notificationUtils) {
        this.employeeRepository = employeeRepository;
        this.lookUpValidation = lookUpValidation;
        this.masterValidation = masterValidation;
        this.jwtService = jwtService;
        this.notificationUtils = notificationUtils;
    }
    /**
     * Delegates the creation of a new employee to the employee repository.
     */
    async addEmployee(createEmployeeObject, userId) {
        await this.lookUpValidation.validateDynamicLookupValues(createEmployeeObject, constants_1.LOOK_UP_DATA);
        await this.masterValidation.validateMasterIds(createEmployeeObject, constants_1.MASTER_DATA);
        const employee = await this.employeeRepository.addEmployee(createEmployeeObject, userId);
        if (employee?.userId && employee?.emailId) {
            const token = this.generatePasswordResetToken(employee.userId);
            await this.sendPasswordResetMail(employee.emailId, token);
        }
        return employee;
    }
    /**
     * Retrieval of all Employee records by delegating the operation to the employee repository
     */
    async getEmployees(sort, search, searchBy, page, limit) {
        try {
            const searchParams = search ? (0, helper_utils_1.mapSearchParams)(search) : undefined;
            const sortParams = sort ? (0, helper_utils_1.mapSortParams)(sort) : undefined;
            const { data, count } = await this.employeeRepository.getEmployees(sortParams, searchParams, searchBy, page, limit);
            return { data, count };
        }
        catch (error) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeNotFound);
        }
    }
    /**
     * Retrieve a single Employee record by ID by delegating the operation to the employee repository
     */
    async getEmployeeById(employeeId) {
        const employee = await this.employeeRepository.getEmployeeById(employeeId);
        if (!employee) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeNotFound);
        }
        return employee;
    }
    /**
     * Retrieve a single Employee record by email_id by delegating the operation to the employee repository
     */
    async getEmployeeByEmailId(emailId) {
        const employee = await this.employeeRepository.getEmployeeByEmailId(emailId);
        if (!employee) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeNotFound);
        }
        return employee;
    }
    /**
     * Update an existing Employee record by ID by delegating the operation to the employee repository
     */
    async updateEmployeeById(employeeId, updateEmployeeObject, userId) {
        await this.lookUpValidation.validateDynamicLookupValues(updateEmployeeObject, constants_1.LOOK_UP_DATA);
        await this.masterValidation.validateMasterIds(updateEmployeeObject, constants_1.MASTER_DATA);
        const updated = await this.employeeRepository.updateEmployeeById(employeeId, updateEmployeeObject, userId);
        if (!updated) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeNotFound);
        }
        return this.getEmployeeById(employeeId);
    }
    /**
     * Delete an Employee record by ID by delegating the operation to the employee repository
     */
    async deleteEmployeeById(employeeId, delegateOwnerUserId, newManagerId) {
        const deleted = await this.employeeRepository.deleteEmployeeById(employeeId, delegateOwnerUserId, newManagerId);
        if (!deleted) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeNotFound);
        }
    }
    /**
     * Retrieve all employees values by delegating the operation to the employee repository
     */
    async getListOfEmployeesValues(sort, search, searchBy, page, limit) {
        const searchParams = search ? (0, helper_utils_1.mapSearchParams)(search) : undefined;
        const sortParams = sort ? (0, helper_utils_1.mapSortParams)(sort) : undefined;
        const { data, count } = await this.employeeRepository.getListOfEmployeesValues(sortParams, searchParams, searchBy, page, limit);
        return { data, count };
    }
    /**
     * Retrieves the employee hierarchy for a given root userId.
     */
    async getEmployeeHierarchyByUserId(userId) {
        const hierarchy = await this.employeeRepository.getEmployeeHierarchyByUserId(userId);
        if (!hierarchy) { // Or check if hierarchy is empty based on expected SQL output
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeHierarchyNotFound);
        }
        return hierarchy;
    }
    /**
     * Retrieves the parent user with the given privlege for the given user.
     */
    async getParentUserWithPrivilege(userId, aclCategoryKey, aclActionKey) {
        const parentUser = await this.employeeRepository.getParentUserWithPrivilege(userId, aclCategoryKey, aclActionKey);
        if (!parentUser || parentUser === null) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeeHierarchyNotFound);
        }
        return parentUser;
    }
    /**
     * Retrieves the users with a given privilege.
     */
    async getAllUsersWithPrivilege(aclCategoryKey, aclActionKey, page, limit) {
        const users = await this.employeeRepository.getAllUsersWithPrivilege(aclCategoryKey, aclActionKey, page, limit);
        if (!users || users === null || users.length === 0) {
            throw new common_1.NotFoundException(messages_1.infoMessages.employeePrivilegeNotFound);
        }
        return users;
    }
    /**
     * Initiates the rebuilding of the flat employee hierarchy.
     * @param creatorId The ID of the user initiating the rebuild.
     */
    async rebuildFlatHierarchy(creatorId) {
        try {
            await this.employeeRepository.rebuildFlatHierarchy(creatorId);
        }
        catch (error) {
            // The repository already throws InternalServerErrorException
            // Let's rethrow it or handle it as per service layer logic
            console.error("Error in EmployeeService during rebuildFlatHierarchy:", error);
            throw error; // Rethrow the exception from the repository
        }
    }
    /**
     * Generates a JWT token for password reset.
     */
    generatePasswordResetToken(emailId, userId) {
        return this.jwtService.sign({ emailId, userId }, {
            secret: environment_1.ENV.JWT_SECRET,
            expiresIn: environment_1.ENV.JWT_RESET_PASSWORD_EXPIRY || '7d',
        });
    }
    /**
     * Sends password reset mail. Currently logs link for demonstration.
     */
    async sendPasswordResetMail(email, userId, firstName) {
        try {
            const payload = {
                email: email,
                passwordResetTokenKey: environment_1.ENV.PASSWORD_RESET_TOKEN_KEY,
                userId: userId,
            };
            const accessToken = await this.jwtService.signAsync(payload, {
                expiresIn: environment_1.ENV.JWT_RESET_PASSWORD_EXPIRY || '7d',
            });
            const url = `${environment_1.ENV.RESET_PASSWORD_URL}?token=${accessToken}`;
            const eventType = constants_2.PASSWORD_RESET_EVENT;
            const channel = constants_2.NOTIFICATION_EMAIL;
            const eventDetails = await this.notificationUtils.getNotificationDetailsByEvent(eventType);
            const parameters = {};
            eventDetails.map((event) => {
                switch (event.parameterKey) {
                    case constants_2.PASSWORD_RESET_KEY_1:
                        parameters[event.parameterKey] = url;
                        break;
                    case constants_2.PASSWORD_RESET_KEY_2:
                        parameters[event.parameterKey] = firstName;
                        break;
                    case constants_2.PASSWORD_RESET_KEY_3:
                        parameters[event.parameterKey] = email;
                        break;
                    default:
                        parameters[event.parameterKey] = '';
                }
            });
            const response = await axios_1.default.post(`${environment_1.ENV.URL_NOTIFICATION_SERVICE}/notifications`, {
                eventType: constants_2.PASSWORD_RESET_EVENT,
                emailId: [email],
                channel: channel,
                parameters: parameters
            });
            return response.data;
        }
        catch (error) {
            console.log(error);
        }
    }
    /**
     * Validates the password reset token and returns payload.
     */
    async validateResetToken(token) {
        try {
            return await this.jwtService.verifyAsync(token, {
                secret: environment_1.ENV.JWT_SECRET,
            });
        }
        catch (error) {
            throw new common_1.ForbiddenException('Invalid or expired token');
        }
    }
    /**
     * Updates the password for the user represented by the token.
     */
    async resetPassword(token, password) {
        const payload = await this.validateResetToken(token);
        const hashed = await (0, password_util_1.hashPassword)(password);
        await this.employeeRepository.updateUserPassword(payload.userId, hashed);
    }
};
exports.EmployeeService = EmployeeService;
exports.EmployeeService = EmployeeService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof employee_repository_1.EmployeeRepository !== "undefined" && employee_repository_1.EmployeeRepository) === "function" ? _a : Object, typeof (_b = typeof lookup_validation_1.LookUpValidationService !== "undefined" && lookup_validation_1.LookUpValidationService) === "function" ? _b : Object, typeof (_c = typeof masters_validation_1.MasterValidationService !== "undefined" && masters_validation_1.MasterValidationService) === "function" ? _c : Object, typeof (_d = typeof jwt_1.JwtService !== "undefined" && jwt_1.JwtService) === "function" ? _d : Object, typeof (_e = typeof notification_utils_1.NotificationUtils !== "undefined" && notification_utils_1.NotificationUtils) === "function" ? _e : Object])
], EmployeeService);
